import { Flex, Tag, Typography } from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useCursorList } from '../../lib/use-cursor-list';
import { formatDate } from '../../lib/format';
import { listQuotations, type Quotation } from './api';
import { formatMoney } from '../../lib/money';
import { QUOTATION_STATUS_LABEL, quotationTone } from './status';

const { Text } = Typography;

export function QuotationListPage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { rows, isFetching, hasMore, loadMore } = useCursorList(['quotations', search], (page) =>
    listQuotations({ ...page, q: search }),
  );

  return (
    <ListPage<Quotation>
      title={t('sales.title')}
      searchPlaceholder={t('sales.search')}
      onSearch={(value) => {
        setSearch(value);
      }}
      loading={isFetching}
      emptyDescription={search.length > 0 ? t('sales.noResults') : t('sales.empty')}
      columns={[
        {
          title: t('sales.fields.quotationNo'),
          dataIndex: 'quotationNo',
          width: 190,
          render: (value: string) => <span className="tabular">{value}</span>,
        },
        { title: t('sales.fields.customer'), dataIndex: 'customerName' },
        {
          title: t('sales.fields.status'),
          dataIndex: 'status',
          width: 180,
          render: (status: Quotation['status']) => (
            <Tag color={quotationTone(status)}>{QUOTATION_STATUS_LABEL[status]}</Tag>
          ),
        },
        {
          title: t('sales.fields.total'),
          dataIndex: 'grandTotal',
          width: 170,
          align: 'right' as const,
          render: (value: string, row: Quotation) => formatMoney(value, row.currency),
        },
        {
          title: t('sales.fields.validUntil'),
          dataIndex: 'validUntil',
          width: 150,
          render: (value: string, row: Quotation) => (
            <Text type={row.isExpired ? 'danger' : undefined}>{formatDate(value)}</Text>
          ),
        },
      ]}
      rows={rows}
      hasMore={hasMore}
      onLoadMore={loadMore}
      rowKey={(row) => row.uid}
      onRowClick={(row) => {
        void navigate(`/quotations/${row.uid}`);
      }}
      renderMobileRow={(row) => (
        <Flex vertical gap={4}>
          <Flex justify="space-between" align="center" gap={8}>
            <Text strong>{row.customerName}</Text>
            <Tag color={quotationTone(row.status)} style={{ marginInlineEnd: 0 }}>
              {QUOTATION_STATUS_LABEL[row.status]}
            </Tag>
          </Flex>
          <Flex justify="space-between" gap={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.quotationNo}
            </Text>
            <Text strong>{formatMoney(row.grandTotal, row.currency)}</Text>
          </Flex>
        </Flex>
      )}
    />
  );
}
